/**
 * Labstep
 */

import { FieldType } from 'labstep-web/constants/form';
import type { FieldWithoutActionProps } from 'labstep-web/core/Form/Reusable/types';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Metadata } from 'labstep-web/models/metadata';
import {
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import type { PostFilterComparisonType } from 'labstep-web/services/postFilter/constants';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadable,
  PostFilterComparisonHumanReadableChronologic,
} from 'labstep-web/services/postFilter/constants';
import rules from 'labstep-web/services/validation/rules';
import { object, string } from 'yup';
import type { FilterMetadataType } from './types';

export const fieldMetadata: FieldWithoutActionProps = {
  elementProps: {
    entityName: Metadata.entityName,
    params: {
      permission_class: ExperimentWorkflow.entityName,
    },
  },
  name: 'metadata',
  fieldType: FieldType.SearchSelect,
  fieldLabel: 'Data Field',
  validation: rules.experiment_workflow.metadata,
};

export const getComparisonLabelDict = (type: FilterMetadataType) =>
  type === MetadataType.date
    ? PostFilterComparisonHumanReadableChronologic
    : PostFilterComparisonHumanReadable;

export const PostFilterMetadatasSupportedTypes = [
  MetadataType.default,
  MetadataType.numeric,
  MetadataType.date,
  MetadataType.options,
];

export const getFieldComparison = (
  options: PostFilterComparisonType[],
  labelByOption: Record<PostFilterComparisonType, string>,
): FieldWithoutActionProps => ({
  fieldLabel: 'Comparison',
  name: 'comparison',
  fieldType: FieldType.ReactSelect,
  elementProps: {
    options: options.map((option) => ({
      value: option,
      label: labelByOption[option],
    })),
  },
  validation: rules.metadata.comparison,
});

export const fieldDate: FieldWithoutActionProps = {
  fieldLabel: 'Date',
  name: 'value',
  fieldType: FieldType.DateTimePicker,
  validation: string().required(),
};

export const fieldDateTime: FieldWithoutActionProps = {
  fieldLabel: 'Date',
  name: 'value',
  fieldType: FieldType.DateTimePicker,
  validation: string().required(),
  elementProps: {
    enableTime: true,
    time_24hr: true,
  },
};

export const fieldValue: FieldWithoutActionProps = {
  fieldType: FieldType.Text,
  fieldLabel: 'Value',
  name: 'value',
  placeholder: 'Enter value',
  validation: string().required(),
};

export const fieldOptions = {
  fieldLabel: 'Options',
  name: 'value',
  fieldType: FieldType.SelectOptions,
  validation: 'required',
};

export const fieldValueWithAutoFocus = {
  ...fieldValue,
  autoFocus: true,
};

export const fieldDateWithAutoFocus = {
  ...fieldDate,
  autoFocus: true,
};

export const fieldDataTimeWithAutoFocus = {
  ...fieldDateTime,
  autoFocus: true,
};

export const fieldsPerMetadataType = (
  autoFocusValue?: boolean,
): Record<
  FilterMetadataType,
  { fields: FieldWithoutActionProps[] }
> => {
  const fieldValueInForm = autoFocusValue
    ? fieldValueWithAutoFocus
    : fieldValue;
  const fieldDateInForm = autoFocusValue
    ? fieldDateWithAutoFocus
    : fieldDate;

  const fieldDateTimeInForm = autoFocusValue
    ? fieldDataTimeWithAutoFocus
    : fieldDateTime;
  return {
    [MetadataType.default]: {
      fields: [fieldValueInForm],
    },
    [MetadataType.numeric]: {
      fields: [
        getFieldComparison(
          [
            PostFilterComparison.eq,
            PostFilterComparison.gt,
            PostFilterComparison.gte,
            PostFilterComparison.lt,
            PostFilterComparison.lte,
          ],
          getComparisonLabelDict(MetadataType.numeric),
        ),
        fieldValueInForm,
      ],
    },
    [MetadataType.date]: {
      fields: [
        getFieldComparison(
          [
            PostFilterComparison.eq,
            PostFilterComparison.gt,
            PostFilterComparison.lt,
          ],
          getComparisonLabelDict(MetadataType.date),
        ),
        fieldDateInForm,
      ],
    },
    [MetadataType.datetime]: {
      fields: [
        getFieldComparison(
          [
            PostFilterComparison.eq,
            PostFilterComparison.gt,
            PostFilterComparison.lt,
          ],
          getComparisonLabelDict(MetadataType.date),
        ),
        fieldDateTimeInForm,
      ],
    },
    [MetadataType.options]: {
      fields: [
        getFieldComparison(
          [
            PostFilterComparison.contains,
            PostFilterComparison.not_contains,
          ],
          getComparisonLabelDict(MetadataType.options),
        ),
        fieldValueInForm,
      ],
    },
    [MetadataType.sequence]: {
      fields: [
        getFieldComparison(
          [
            PostFilterComparison.contains,
            PostFilterComparison.not_contains,
          ],
          getComparisonLabelDict(MetadataType.sequence),
        ),
        fieldValueInForm,
      ],
    },
  };
};

export const fieldLabel: FieldWithoutActionProps = {
  fieldType: FieldType.Text,
  fieldLabel: 'Field Name',
  name: 'label',
  placeholder: 'Enter field name',
  validation: string().required(),
};

export const fieldType: FieldWithoutActionProps = {
  fieldLabel: 'Type',
  name: 'type',
  placeholder: MetadataTypeValues.default,
  fieldType: FieldType.ReactSelect,
  elementProps: {
    options: PostFilterMetadatasSupportedTypes.map(
      (metadataType) => ({
        value: metadataType,
        label: MetadataTypeValues[metadataType],
      }),
    ),
  },
  validation: object(),
};

export const fieldsGeneric = [fieldLabel, fieldType];
